import { Events } from '@APP/events/app-events';
import { requires } from '@APP/mixins/dependency';
import { ReactiveList } from '@APP/model/reactive-list';
import { eventBus } from '@APP/utils/event-bus';
import { BaseService } from './base-service';
import { IDataProviderService } from './data-provider';
import { QuestionsService } from './questions-service';

type DEPS = {
  questionsService: QuestionsService;
};

// Reactive picklist source for the COMP/COLL deductible questions.
// Rules live on the model (see getAllowedDeductibleOptions / getDefaultDeductible
// on ServerQuestionsModel). This service watches the two questions that steer
// those rules — Physical_Damage_Settlement_option and External_Agent — and
// repopulates the list when either one changes.
//
// Backend already substitutes Coherent's $500 with the spec default $1,000 in
// getPackageData, so on tier selection the questionnaire receives $1,000 for
// COMP/COLL. This service handles two remaining concerns:
//   - Filter the dropdown options shown in the picklist per the spec
//     (hide $100/$250/$500 for Agreed Value & Replacement Cost;
//      hide $100/$250 for ACV + direct customers).
//   - When the user changes Settlement Option or the customer switches from
//     direct to agent flow, snap the current deductible to the model default
//     if it's outside the newly-allowed set.
@requires(QuestionsService.serviceName)
class DeductiblesDataProvider
  extends BaseService<DEPS>
  implements IDataProviderService {
  static serviceName = 'deductiblesDataProvider';

  private deductibles: ReactiveList<string, any> = new ReactiveList('deductibles');

  constructor() {
    super();
    eventBus().on(Events.Type.QuestionUpdate, (event) => {
      if (
        event.questionId === 'Physical_Damage_Settlement_option' ||
        event.questionId === 'External_Agent'
      ) {
        this.populateList();
        this.snapDeductibleIfInvalid();
      }
    });
  }

  getList(name: string): ReactiveList<any, any> {
    if (name !== 'deductibleOptions') {
      throw new Error('Unknown list');
    }
    this.populateList();
    // Returning-user spec: whatever the user previously selected must be shown
    // as-is. So on initial mount we only seed the model default when the
    // questionnaire's COMP/COLL is genuinely empty (fresh app before any tier
    // was picked). Any persisted value — legal or illegal under the current
    // Settlement/agent combo — is preserved here. The stricter "snap if the
    // value falls out of the newly-allowed set" logic still runs when the
    // user changes Settlement Option or the agent flag flips (see
    // constructor); that's the only place an already-persisted value can
    // become illegal, and the user is actively causing the transition.
    this.snapDefaultIfEmpty();
    return this.deductibles;
  }

  private populateList() {
    this.deductibles.setState('pending');
    this.deductibles.options = this.deps.questionsService.model.getAllowedDeductibleOptions();
    this.deductibles.setState('ready');
  }

  // Fresh-app seed: set COMP/COLL to the model default only when the field is
  // completely empty. Used on initial-mount so first-time users see the spec
  // default while returning users keep whatever they had selected.
  private snapDefaultIfEmpty() {
    const model = this.deps.questionsService.model as any;
    const defaultValue = model.getDefaultDeductible();
    ['COMP_Coverage_Deductible_Amount', 'COLL_Coverage_Deductible_Amount'].forEach((qid) => {
      const q = model.getQuestionById(qid);
      if (!q) return;
      const current = String(q.value ?? '');
      if (!current) {
        q.value = defaultValue;
      }
    });
  }

  // Snap COMP/COLL deductible to the model default only when the current value
  // is empty or has fallen outside the currently-allowed set. A user-picked
  // value that remains legal is preserved. Used on Settlement Option /
  // External_Agent changes, where a legal value may have become illegal.
  private snapDeductibleIfInvalid() {
    const model = this.deps.questionsService.model as any;
    const allowed = model.getAllowedDeductibleOptions();
    const defaultValue = model.getDefaultDeductible();
    const allowedValues = allowed.map((o: { value: string }) => o.value);
    ['COMP_Coverage_Deductible_Amount', 'COLL_Coverage_Deductible_Amount'].forEach((qid) => {
      const q = model.getQuestionById(qid);
      if (!q) return;
      const current = String(q.value ?? '');
      if (!current || !allowedValues.includes(current)) {
        q.value = defaultValue;
      }
    });
  }
}

export { DeductiblesDataProvider };

DeductiblesDataProvider.define();
